import { PatientConsentContext } from '@/context/PatientConsentProvider';
import { BrandThemeProvider } from '@montugroup/themes';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, MenuList, SwipeableDrawer, Toolbar, styled } from '@mui/material';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

import { alternaleafAppBarConfig } from '@/components/layout/navbar/navbar.config';
import AltLogo from '@montugroup/circuit-shared/components/common/AltLogo';

import Logout from '../sidebar/Logout';
import { OrderButton } from '../sidebar/Sidebar';

import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import { useCreateOrderButton } from '@/hooks/useCreateOrderButton';
import useCustomer from '@/hooks/user/useCustomer';
import { HOME_ROUTE } from '@/routes/route-paths';
import { NavMenuItem } from '@montugroup/circuit-shared';
import { Box, IconButton } from '@montugroup/design-system';

const NavigationBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: '100%',
  boxShadow: 'none',
}));

const NavToolbar = styled(Toolbar)(({ theme }) => ({
  padding: theme.spacing(4),
  justifyContent: 'space-between',
  gap: theme.spacing(8),
  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(5),
  },
}));

const MenuIconContainer = styled(Box)`
  display: flex;
`;

function MobileAppBar() {
  const { key: partnerKey } = usePortalPartnerBrand();
  const createOrderButton = useCreateOrderButton();
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: customer } = useCustomer();
  const isDischarged = customer?.isDischarged;

  const { requiresConsentSubmission } = useContext(PatientConsentContext);
  const showOrderButton = !isDischarged && !requiresConsentSubmission;

  const toggleMenu = () => {
    setMenuOpen((prevState) => !prevState);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  const handleLinkClick = () => {
    scrollToTop();
    toggleMenu();
  };

  return (
    <NavigationBar>
      <NavToolbar>
        <Link to={HOME_ROUTE} onClick={scrollToTop}>
          <AltLogo />
        </Link>

        <MenuIconContainer>
          <IconButton
            size="small"
            aria-haspopup="true"
            aria-label="menu"
            onClick={toggleMenu}
            sx={{
              color: (theme) => theme.palette.primary.main,
              '&:focus': {
                outline: 'none',
              },
            }}
          >
            <MenuIcon
              sx={{
                height: 35,
                width: 35,
                color: (theme) => theme.palette.common.white,
              }}
            />
          </IconButton>
        </MenuIconContainer>
      </NavToolbar>
      <SwipeableDrawer
        anchor="right"
        open={menuOpen}
        onClose={toggleMenu}
        onOpen={toggleMenu}
        slotProps={{
          paper: {
            sx: {
              width: '100%',
              backgroundColor: (theme) => theme.palette.primary.main,
            },
          },
        }}
      >
        <NavToolbar
          sx={{
            marginTop: (theme) => theme.spacing(1),
          }}
        >
          <Link to={HOME_ROUTE} onClick={toggleMenu}>
            <AltLogo />
          </Link>
          <MenuIconContainer>
            <IconButton onClick={toggleMenu}>
              <CloseIcon
                sx={{
                  color: (theme) => theme.palette.common.white,
                }}
              />
            </IconButton>
          </MenuIconContainer>
        </NavToolbar>
        <MenuList>
          {alternaleafAppBarConfig.map((mainMenuItem) => (
            <NavMenuItem key={mainMenuItem.id} menuItem={mainMenuItem} onClick={handleLinkClick} />
          ))}
        </MenuList>
        {showOrderButton && (
          <Box paddingX={(theme) => theme.spacing(4)} mb={6} mt="auto">
            <BrandThemeProvider theme={partnerKey}>
              <Link to={createOrderButton.link} onClick={handleLinkClick}>
                <OrderButton variant="contained" size="large" color="primary">
                  {createOrderButton.icon && <createOrderButton.icon />}
                  {createOrderButton.name}
                </OrderButton>
              </Link>
            </BrandThemeProvider>
          </Box>
        )}
        <Logout />
      </SwipeableDrawer>
    </NavigationBar>
  );
}

export default MobileAppBar;
